/* =========================================================================
   telemetry-table.utils.ts
   Pure utility functions and constants for Telemetry Data & Stream Tables
   ========================================================================= */

import {
  tableColDef,
  tableColValueAt,
  fmt,
} from './telemetry-helpers.utils';
import { resolveEnumValue, isBooleanEnumState } from './telemetry-enum.utils';

export const DEFAULT_ROW_HEIGHT = 31;
export const DEFAULT_COL_WIDTH = 140;
export const DEFAULT_TIME_COL_WIDTH = 100;
export const OVERSCAN_ROWS = 5;
export const OVERSCAN_COLS = 4;

export interface FormattedTableCell {
  formattedVal: string;
  enumState: 'on' | 'off' | null;
  rawValForEnum: number | string | null;
  isPlaceholder: boolean;
}

/**
 * Formats a single table cell value, evaluating enum mappings or numeric unit conversions.
 */
export function formatTableCellValue(
  targetCol: any,
  targetTime: number | null | undefined,
  panelId: string,
  units: any,
  precision: number
): FormattedTableCell {
  let formattedVal = '—';
  let enumState: 'on' | 'off' | null = null;
  let rawValForEnum: number | string | null = null;
  let isPlaceholder = true;

  if (targetCol && targetTime != null) {
    const def = tableColDef(targetCol);
    const val = tableColValueAt(targetCol, targetTime, panelId);
    isPlaceholder = val === undefined || val === null;
    if (!isPlaceholder) {
      const enumVal = resolveEnumValue(val, def?.enumValues);
      if (enumVal) {
        formattedVal = `${enumVal} (${val})`;
        enumState = isBooleanEnumState(enumVal);
        rawValForEnum = val ?? null;
      } else {
        formattedVal = fmt(val, def, units, precision);
      }
    }
  }

  return {
    formattedVal,
    enumState,
    rawValForEnum,
    isPlaceholder,
  };
}

export interface TableScrollBoundaryResult {
  hasReachedEnd: boolean;
  hasReachedStart: boolean;
}

/**
 * Pure calculation to determine if table scroll has reached boundary thresholds
 * for historical or live data fetching.
 */
export function detectTableScrollBoundary(
  target: HTMLDivElement,
  prevSt: number,
  prevSl: number,
  isStream: boolean,
  isAsc: boolean
): TableScrollBoundaryResult {
  const st = target.scrollTop;
  const sl = target.scrollLeft;

  if (isStream) {
    const hasVerticalScroll = target.scrollHeight > target.clientHeight + 40;
    const isScrollingDown = st > prevSt;
    const isScrollingUp = st < prevSt;
    const isAtBottom = target.scrollHeight - (st + target.clientHeight) <= 40;
    const isAtTop = st <= 10;
    const hasReachedEnd = Boolean(hasVerticalScroll && (isAsc ? (isScrollingUp && isAtTop) : (isScrollingDown && isAtBottom)));
    const hasReachedStart = Boolean(isAsc ? (isScrollingDown && isAtBottom) : (isScrollingUp && isAtTop));
    return { hasReachedEnd, hasReachedStart };
  } else {
    const hasHorizontalScroll = target.scrollWidth > target.clientWidth + 40;
    const isScrollingRight = sl > prevSl;
    const isScrollingLeft = sl < prevSl;
    const isAtRightEnd = target.scrollWidth - (sl + target.clientWidth) <= 40;
    const isAtLeft = sl <= 10;
    const hasReachedEnd = Boolean(hasHorizontalScroll && isScrollingRight && isAtRightEnd);
    const hasReachedStart = Boolean(isScrollingLeft && isAtLeft);
    return { hasReachedEnd, hasReachedStart };
  }
}
